// src/components/Sidebar.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Sidebar.css";
import bhashini_logo from "../images/bhashini_logo.png";
import bookmark_icon from "../images/bookmark_icon.png";
import dashboard_icon from "../images/dashboard_icon.png";
import library_icon from "../images/library_icon.png";
import settings_icon from "../images/settings_icon.png";
import help_icon from "../images/help_icon.png";
import logout_icon from "../images/logout_icon.png";
import bookmark_active from "../images/bookmark_active.png";
import dashboard_active from "../images/dashboard_active.png";
import library_active from "../images/library_active.png";
import settings_active from "../images/settings_active.png";
import { getToken, setToken } from "../storage/Storage";
import { speakText, enableSpeech, disableSpeech } from "./utils/speechUtils"; // <-- added

const Sidebar = ({ activeItem, setActiveItem, resetSearch }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [token, setTokenState] = useState(null);
  const [isAudioOn, setIsAudioOn] = useState(false);
  const navigate = useNavigate();
  const menuItemRefs = useRef([]);

  useEffect(() => {
    let mounted = true;

    const fetchToken = async () => {
      try {
        const storedToken = await getToken();
        if (!mounted) return;
        setTokenState(storedToken);
      } catch (error) {
        console.error("Failed to fetch token", error);
      }
    };
    fetchToken();

    // initialize audio mode from localStorage (sync with Settings)
    try {
      const raw = localStorage.getItem("isAudioModeOn");
      const parsed = raw !== null ? JSON.parse(raw) : false;
      setIsAudioOn(parsed);

      // ensure speech engine matches stored value on mount
      if (parsed) enableSpeech();
      else disableSpeech();
    } catch (err) {
      setIsAudioOn(false);
      disableSpeech();
    }

    // Listen for changes from other tabs/components (Settings toggling)
    const handleStorage = (e) => {
      if (e.key === "isAudioModeOn") {
        try {
          const newVal = e.newValue !== null ? JSON.parse(e.newValue) : false;
          setIsAudioOn(newVal);
          if (newVal) enableSpeech();
          else disableSpeech();
        } catch (err) {
          // ignore malformed values
        }
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      mounted = false;
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await setToken("");
      // turn off speech on logout to be safe
      localStorage.setItem("isAudioModeOn", JSON.stringify(false));
      setIsAudioOn(false);
      disableSpeech();
      navigate("/");
    } catch (error) {
      console.error("Error logging out", error);
    }
  };

  const menuItems = [
    { icon: dashboard_icon, activeIcon: dashboard_active, label: "Dashboard", key: "dashboard" },
    { icon: bookmark_icon, activeIcon: bookmark_active, label: "Bookmark", key: "bookmarks" },
    { icon: library_icon, activeIcon: library_active, label: "Library", key: "library" },
    { icon: settings_icon, activeIcon: settings_active, label: "Settings", key: "settings" },
  ];

  const bottomMenuItems = [
    { icon: help_icon, label: "Help", key: "help", disabled: false },
    { icon: logout_icon, label: "Logout", key: "logout", disabled: false },
  ];

  const handleItemClick = (item) => {
    if (item.key === "logout") {
      handleLogout();
    } else if (!item.disabled) {
      setActiveItem(item.key);
      if (item.key === "home") resetSearch();
      // optionally navigate here if you want immediate route change
      // navigate(`/${item.key}`);
    }
  };

  const handleKeyDown = (event, item) => {
    if (event.key === "Enter") handleItemClick(item);
  };

  // only speak when audio mode is ON
  const handleImageFocus = (event) => {
    if (!isAudioOn) return;
    const altText = event.target.alt;
    speakText(altText);
  };

  const toggleAudioMode = () => {
    try {
      const next = !isAudioOn;
      setIsAudioOn(next);
      localStorage.setItem("isAudioModeOn", JSON.stringify(next));
      if (next) {
        enableSpeech();
        // brief audible feedback when turning on
        speakText("Audio mode on");
      } else {
        disableSpeech();
      }
    } catch (err) {
      console.error("Failed to toggle audio mode", err);
    }
  };

  const AudioOnIcon = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path d="M3 10v4h4l5 5V5L7 10H3z" fill="currentColor" />
      <path d="M16.5 8.5a4.5 4.5 0 010 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19 6a7 7 0 010 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const AudioOffIcon = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path d="M3 10v4h4l5 5V5L7 10H3z" fill="currentColor" />
      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div className="sidebar">
      <div className="sidebar-top">
        <div className="logo-container">
          <a href="/">
            <img
              src={bhashini_logo}
              alt="Welcome to Drishti Library"
              className="logo"
              tabIndex={0}
              onFocus={handleImageFocus}
            />
          </a>
        </div>

        {menuItems
          .filter((item) => token || item.key === "dashboard")
          .map((item, index) => (
            <div
              key={item.key}
              ref={(el) => (menuItemRefs.current[index] = el)}
              tabIndex={0}
              className={`sidebar-icon ${activeItem === item.key ? "active" : ""}`}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setHoveredItem(item.key)}
              onMouseLeave={() => setHoveredItem(null)}
              onFocus={() => isAudioOn && speakText(item.label)}
              onKeyDown={(e) => handleKeyDown(e, item)}
            >
              <img
                src={activeItem === item.key || hoveredItem === item.key ? item.activeIcon : item.icon}
                alt={`${item.label} Icon`}
              />
            </div>
          ))}
      </div>

      {token && (
        <div className="sidebar-bottom">
          {/* Audio Mode button - placed above help */}
          <div
            className={`sidebar-icon audio-button ${isAudioOn ? "audio-on" : "audio-off"}`}
            tabIndex={0}
            role="button"
            aria-pressed={isAudioOn}
            aria-label={`Audio mode ${isAudioOn ? "on" : "off"}`}
            onClick={toggleAudioMode}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleAudioMode();
              }
            }}
            onFocus={() => isAudioOn && speakText(`Audio mode ${isAudioOn ? "on" : "off"}`)}
          >
            {isAudioOn ? <AudioOnIcon /> : <AudioOffIcon />}
          </div>

          {/* Help and Logout icons */}
          {bottomMenuItems.map((item, index) => {
            const idx = menuItems.length + 1 + index;
            return (
              <div
                key={item.key}
                ref={(el) => (menuItemRefs.current[idx] = el)}
                tabIndex={0}
                className={`sidebar-icon ${activeItem === item.key ? "active" : ""}`}
                onClick={() => handleItemClick(item)}
                onFocus={() => isAudioOn && speakText(item.label)}
                onKeyDown={(e) => handleKeyDown(e, item)}
              >
                <img src={item.icon} alt={`${item.label} Icon`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
